/*
 * Shared styles for components in src/components/common/.
 * Single module by design — these helpers are tiny and tightly related;
 * splitting per-component would fragment the design surface without benefit.
 */

/* TokenIcon */
.iconFrame {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  flex: 0 0 auto;
}

.icon {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  object-fit: cover;
  background: var(--surface-3);
  display: inline-block;
  vertical-align: middle;
  flex: 0 0 auto;
  transition: opacity 120ms ease-out;
}

.iconFallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  border-radius: 50%;
  background: var(--surface-3);
  border: 1px solid var(--border);
  color: var(--text-md);
  flex: 0 0 auto;
  font-family: var(--font-mono);
  font-size: max(8px, calc(var(--token-icon-size, 24px) * 0.34));
  font-weight: 600;
  line-height: 1;
  overflow: hidden;
  user-select: none;
}

.iconFallbackText {
  max-width: 100%;
  transform: translateY(0.02em);
}

@media (prefers-reduced-motion: reduce) {
  .icon {
    transition: none;
  }
}

/* TickerLabel */
.ticker {
  display: inline-block;
  max-width: 18ch;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
  white-space: nowrap;
  font-weight: 600;
  color: var(--text-hi);
  letter-spacing: 0;
}

.tickerFallback {
  font-family: var(--font-mono);
  font-size: 0.85em;
  color: var(--text-md);
}

/* PriceChange */
.up {
  color: var(--buy);
  font-variant-numeric: tabular-nums;
}

.down {
  color: var(--sell);
  font-variant-numeric: tabular-nums;
}

.muted {
  color: var(--text-md);
}

/* Skeleton */
.skeleton {
  display: inline-block;
  background: linear-gradient(
    90deg,
    var(--surface-3) 0%,
    var(--surface-2) 50%,
    var(--surface-3) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border-radius: var(--radius-sm);
}

.skeletonRound {
  border-radius: 50%;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* TabBar */
.tabBar {
  display: inline-flex;
  /* Long tab sets (the 10-interval chart row) wrap instead of
     overflowing their card. */
  flex-wrap: wrap;
  gap: 2px;
  padding: 3px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
}

.tab {
  appearance: none;
  background: transparent;
  border: 1px solid transparent;
  min-height: 30px;
  padding: 0 11px;
  border-radius: var(--radius-sm);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-md);
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}

.tab:hover {
  color: var(--text-hi);
  background: var(--surface-3);
}

.tab:disabled,
.tab[aria-disabled="true"] {
  cursor: not-allowed;
  opacity: 0.5;
}

.tabActive {
  background: var(--accent-weak);
  color: var(--text-hi);
  border-color: var(--accent-line);
}

/* ErrorBanner — on Soft: a quiet red tint on the block's ground, no rim (a
   wash with a rim is the look the Soft surfaces left); the failed toast's mark
   before the words; Retry the raised atom, a size down. */
.errorBanner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 44px;
  padding: 6px 8px 6px 14px;
  border-radius: 16px;
  background: color-mix(in srgb, var(--soft-down) 9%, var(--soft-block));
  color: var(--soft-text);
  font-family: var(--font-soft);
  font-size: 13px;
  font-weight: var(--fw-label);
  line-height: 18px;
}

.errorText {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.errorText svg {
  flex: none;
  color: var(--soft-down);
}

/* over the tint the raised atom keeps its own tone; 32 high, as a small disc is */
.errorBanner .retryBtn {
  flex: none;
  height: 32px;
  font-size: 13px;
}
